import { Inject } from '@odinjs/odin';
import { ContextualBarAPI, DeckAPI, DeckValue, LayoutAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnMount, OnSelectItem, OnUnmount, OnUnselectItem } from '@philips-internal/oswald-ui-core/lib/client';
import { MountEvent, SelectItemEvent, UnselectItemEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { MyChartSideBarItemType } from '../../constants/my-chart-side-bar-item-type';
import BaseController from '../BaseController';
import { applicationContext } from '@philips-internal/oswald-ui-foundation/lib/context';
import { isMobile } from '@philips-internal/oswald-ui-utils';

import './my-chart-main-controller.css';
import { MyChartUtils } from '../../utils/my-chart-utils';
import { FragmentNames } from '../../codegen';
import { MyChartMainAliases } from '../../codegen';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { defaultIfUndefined } from '../../utils/object-utils';
import { MyChartControlItems } from '../../constants/my-chart-tab-control-items';
import HospitalTabVisibility from 'src/interfaces/hospital-tab-visibility';

interface SideBarItemProps extends DeckValue {
  attributeToShowItem: TypeSideBarItemsToShow;
  image?: string;
  showItem: string;
  title: string;
  type: MyChartSideBarItemType;
}

interface SideBarItemsToShow {
  showAllergies: string;
  showDischargeSummary: string;
  showDocument: string
  showMedicalPrescription: string;
  showMedicines: string;
  showPastHistory: string;
  showSickNote: string;
  showTeleconsultationRecords: string;
  showVaccines: string;
}

enum TypeSideBarItemsToShow {
  SHOW_DOCUMENT = 'showDocument',
  SHOW_MEDICAL_PRESCRIPTION = 'showMedicalPrescription',
  SHOW_SICK_NOTE = 'showSickNote',
  SHOW_ALLERGIES = 'showAllergies',
  SHOW_DISCHARGE_SUMMARY = 'showDischargeSummary',
  SHOW_PAST_HISTORY = 'showPastHistory',
  SHOW_MEDICINE = 'showMedicines',
  SHOW_VACCINES = 'showVaccines',
  SHOW_TELEHEALTH_RECORDS = 'showTeleconsultationRecords'
}

@Controller({ fragment: FragmentNames.MY_CHART_MAIN })
export default class MyChartMainController extends BaseController {

  @Inject(MyChartMainAliases.LAYOUT_GENERAL)
    layoutGeneral!: LayoutAPI;

  private unListeners: { (): void; }[] = [];

  sideBarItems: SideBarItemProps[] = [
    {
      attributeToShowItem: TypeSideBarItemsToShow.SHOW_MEDICAL_PRESCRIPTION,
      image: '/static/assets/icons/palliative-care.svg',
      relasedByBackend: false,
      showItem: 'N',
      title: 'patient.label.prescription',
      type: MyChartSideBarItemType.MEDICAL_PRESCRIPTIONS,
    },
    {
      attributeToShowItem: TypeSideBarItemsToShow.SHOW_SICK_NOTE,
      image: '/static/assets/icons/DLS_Attachment_24.svg',
      relasedByBackend: false,
      showItem: 'N',
      title: 'patient.label.sick-note',
      type: MyChartSideBarItemType.SICK_NOTE,
    },
    {
      attributeToShowItem: TypeSideBarItemsToShow.SHOW_ALLERGIES,
      image: '/static/assets/icons/medicine.svg',
      showItem: 'N',
      title: 'patient.label.allergies',
      type: MyChartSideBarItemType.ALLERGIES,
    },
    {
      attributeToShowItem: TypeSideBarItemsToShow.SHOW_MEDICINE,
      image: '/static/assets/icons/medicine.svg',
      showItem: 'N',
      title: 'patient.label.medicines-in-use',
      type: MyChartSideBarItemType.MEDICINE,
    },
    {
      attributeToShowItem: TypeSideBarItemsToShow.SHOW_VACCINES,
      showItem: 'N',
      title: 'patient.vaccines',
      type: MyChartSideBarItemType.VACCINES
    },
    {
      attributeToShowItem: TypeSideBarItemsToShow.SHOW_DISCHARGE_SUMMARY,
      showItem: 'N',
      title: 'patient.label.encounter-summary',
      type: MyChartSideBarItemType.DISCHARGE_SUMMARY,
    },
    {
      attributeToShowItem: TypeSideBarItemsToShow.SHOW_PAST_HISTORY,
      showItem: 'N',
      title: 'patient.label.past-history',
      type: MyChartSideBarItemType.PAST_HISTORY,
    },
    {
      attributeToShowItem: TypeSideBarItemsToShow.SHOW_DOCUMENT,
      image: '/static/assets/icons/DLS_Attachment_24.svg',
      showItem: 'N',
      title: 'patient.label.changing-documents',
      type: MyChartSideBarItemType.SENDING_DOCUMENTS,
    },
    {
      attributeToShowItem: TypeSideBarItemsToShow.SHOW_TELEHEALTH_RECORDS,
      relasedByBackend: false,
      showItem: 'N',
      title: 'patient.label.teleconsultation-records',
      type: MyChartSideBarItemType.TELECONSULTATION_RECORDS,
    }
  ];

  sideBarMainOption!: SideBarItemProps;
  deckItemsSideBar!: DeckAPI;

  getSideBarItemsReleasedByTasy(sideBarItemsToShow: SideBarItemsToShow, sideBarItems: SideBarItemProps[]): SideBarItemProps[] {
    const newSideBarItemsReleased = sideBarItems.map(item => {
      return {
        ...item,
        showItem: defaultIfUndefined(sideBarItemsToShow[item.attributeToShowItem], item.showItem),
      }
    }).filter(item => item.showItem === 'S');

    this.sideBarItems = newSideBarItemsReleased;
    return newSideBarItemsReleased;
  }

  handleLayoutMyChartMainLoading(sideBarItemsToShow: SideBarItemProps[]): void {
    this.sideBarMainOption = sideBarItemsToShow[0];
    this.historyService.addListener(path => {
      if (path === APPLICATION_ROUTES.MY_CHART && !isMobile()) {
        this.pushToItemRoute(this.sideBarMainOption);
        this.selectItemOnSideBar(this.deckItemsSideBar, this.sideBarMainOption);
      }
      return Promise.resolve();
    });

    this.adjustMyChartWhenLoadingMobile();
  }

  @OnUnmount({ target: MyChartMainAliases.LAYOUT_MY_CHART_MAIN })
  handleLayoutMyChartMainDestroy(): void {
    this.unListeners.forEach(unlistener => {
      unlistener();
    })
  }

  @OnMount({ target: MyChartMainAliases.DECK_ITEMS_SIDEBAR })
  handleDeckItemsSidebarLoading(event: MountEvent): void {
    const deckAPI = event.target as DeckAPI;
    deckAPI.setLoading(true);

    HttpService.get(APPLICATION_ENDPOINTS.GET_MC_ITENS_TO_SHOW, { }).then(response => {
      const sideBarItemsToShow = response.body as SideBarItemsToShow;
      const sideBarItems = this.getSideBarItemsReleasedByTasy(sideBarItemsToShow, this.sideBarItems);
      this.handleLayoutMyChartMainLoading(sideBarItems);
      deckAPI.setData(sideBarItems);

      this.deckItemsSideBar = deckAPI;
      this.selectItemOnSideBar(deckAPI, this.sideBarMainOption);
    }).finally(() => {
      deckAPI.setLoading(false);
      deckAPI.setDone(true);
    });
  }

  selectItemOnSideBar = (deckAPI: DeckAPI, item: SideBarItemProps): void => {
    if (!deckAPI || isMobile() || !item) {
      return;
    }

    setTimeout(() => {
      deckAPI.selectCard(item.type.toString());
      deckAPI.onSelectItem.fire(new SelectItemEvent({ item }));
    });
  }

  @OnMount({ target: MyChartMainAliases.CONTEXTUAL_SIDEBAR })
  handleContextualBarLoading(event: MountEvent): void {
    const contextualBarAPI = event.target as ContextualBarAPI;
    if (isMobile()) {
      contextualBarAPI.setTitle('');
    }
  }

  @OnSelectItem({ target: 'deck-items-sidebar' })
  handleDeckItemSidebarItemSelected(event: SelectItemEvent<SideBarItemProps>): void {
    this.pushToItemRoute(event.item);
  }

  private async pushToItemRoute(item: SideBarItemProps): Promise<void> {

    let toRoute;
    switch (item.type) {
      case MyChartSideBarItemType.SENDING_DOCUMENTS:
        toRoute = APPLICATION_ROUTES.MY_CHART_SENDING_DOCUMENTS;
        break;
      case MyChartSideBarItemType.MEDICAL_PRESCRIPTIONS:
        toRoute = APPLICATION_ROUTES.MY_CHART_PRESCRIPTIONS;
        break;
      case MyChartSideBarItemType.SICK_NOTE:
        toRoute = APPLICATION_ROUTES.MY_CHART_SICK_NOTE;
        break;
      case MyChartSideBarItemType.DISCHARGE_SUMMARY:
        toRoute = APPLICATION_ROUTES.MY_CHART_DISCHARGE_SUMMARY;
        break;
      case MyChartSideBarItemType.MEDICINE:
        toRoute = APPLICATION_ROUTES.MY_CHART_MEDICINE;
        await this.instanceHospitalTabRule(MyChartControlItems.CURRENT_MEDICINES, MyChartControlItems.MEDICINES_HOSPITAL_VISIBILITY);
        break;
      case MyChartSideBarItemType.ALLERGIES:
        toRoute = APPLICATION_ROUTES.MY_CHART_ALLERGIES;
        await this.instanceHospitalTabRule(MyChartControlItems.ALLERGIES, MyChartControlItems.ALLERGIES_HOSPITAL_VISIBILITY);
        break;
      case MyChartSideBarItemType.PAST_HISTORY:
        toRoute = APPLICATION_ROUTES.MY_CHART_PAST_HISTORY;
        await this.instanceHospitalTabRule(MyChartControlItems.PAST_HISTORY, MyChartControlItems.PAST_HISTORY_HOSPITAL_VISIBILITY);
        break;
      case MyChartSideBarItemType.VACCINES:
        toRoute = APPLICATION_ROUTES.MY_CHART_VACCINES;
        break;
      case MyChartSideBarItemType.TELECONSULTATION_RECORDS:
        toRoute = APPLICATION_ROUTES.MY_CHART_TELEHEALTH_RECORDS;
        break;
    }

    const fromRoute = this.historyService.getCurrentRoute();
    if (toRoute && toRoute !== fromRoute) {
      MyChartUtils.pushToSubRoute(toRoute);
    }
  }

  private adjustMyChartWhenLoadingMobile(): void {
    if (isMobile()) {
      this.layoutGeneral.setTemplateAreas('"sidebar"');
      this.layoutGeneral.setColumns('auto');

      const unListenerRedirect = applicationContext.addRedirectListener(path => {
        if (path.path === APPLICATION_ROUTES.MY_CHART) {
          this.layoutGeneral.setTemplateAreas('"sidebar"');
        } else {
          this.layoutGeneral.setTemplateAreas('"content"');
        }
      });

      this.unListeners.push(unListenerRedirect);
    }
  }

  @OnUnselectItem({ target: 'deck-items-sidebar' })
  handleDeckItemsSideBarUnSelected(event: UnselectItemEvent<SideBarItemProps>): void {
    const deckApi = event.target as unknown as DeckAPI;
    deckApi.selectCards([String(event.item.type)])
    this.pushToItemRoute(event.item);
  }

  private async instanceHospitalTabRule(item: string, tab: string): Promise<void> {
    const response = await HttpService.get(APPLICATION_ENDPOINTS.GET_MC_HOSPITAL_ITENS_TO_SHOW, { myChartItem: item });
    const hospitalTabVisibility = response.body as HospitalTabVisibility;
    const body = {
      tabName: tab,
      visibility: hospitalTabVisibility.visibility
    };
    this.context.set({ tabVisibility : body });
  }
}
